import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Clock,
  CheckCircle,
  Sparkles,
  Calculator,
  MapPin,
  Brain,
  Activity,
  Shield,
  Award
} from "lucide-react";
import { Header, Footer } from "../../../../components/Layout";

const CognitivoConductualPage = () => {
  const principles = [
    {
      title: "Lo mejor de dos mundos",
      description: "Combina la reestructuración de pensamientos (cognitivo) con el cambio de comportamientos (conductual) para resultados más completos.",
      icon: Sparkles
    },
    {
      title: "Mayor evidencia científica",
      description: "Es el tratamiento con más respaldo en investigación para adicciones. Múltiples estudios demuestran su efectividad a largo plazo.",
      icon: Award
    },
    {
      title: "Pensar → Sentir → Actuar",
      description: "Trabaja la cadena completa: identifica pensamientos, comprende emociones y modifica comportamientos problemáticos.",
      icon: Brain
    },
    {
      title: "Prevención de recaídas",
      description: "Especial énfasis en identificar situaciones de riesgo y desarrollar estrategias concretas para manejarlas.",
      icon: Shield
    }
  ];

  const techniques = [
    "Registro y análisis de pensamientos automáticos",
    "Reestructuración de creencias sobre la adicción",
    "Identificación de triggers y cadenas de consumo",
    "Entrenamiento en habilidades de afrontamiento",
    "Técnicas de prevención de recaídas (modelo Marlatt)",
    "Manejo del craving con técnicas cognitivo-conductuales",
    "Role-playing de situaciones de riesgo",
    "Planificación de actividades alternativas"
  ];

  const phases = [
    { name: "Evaluación", duration: "1-2 semanas", description: "Análisis funcional del consumo, identificación de patrones y triggers" },
    { name: "Psicoeducación", duration: "2-4 semanas", description: "Entender el modelo cognitivo-conductual de la adicción" },
    { name: "Intervención", duration: "2-4 meses", description: "Trabajo activo en pensamientos, emociones y comportamientos" },
    { name: "Consolidación", duration: "1-2 meses", description: "Práctica y generalización de lo aprendido" },
    { name: "Prevención de recaídas", duration: "Continuo", description: "Plan personalizado para mantener la recuperación" }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Terapia Cognitivo-Conductual para Adicciones",
    "description": "Tratamiento con mayor evidencia científica que combina reestructuración cognitiva con modificación de comportamientos para superar adicciones."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Residencial", "item": "https://sinadicciones.org/tratamiento/residencial" },
      { "@type": "ListItem", "position": 4, "name": "Cognitivo-Conductual", "item": "https://sinadicciones.org/tratamiento/residencial/cognitivo-conductual" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Terapia Cognitivo-Conductual (TCC) para Adicciones | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento cognitivo-conductual residencial para adicciones. El enfoque con mayor evidencia científica. Combina reestructuración cognitiva y modificación de conductas." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/residencial/cognitivo-conductual" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/residencial/cognitivo-conductual" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">Tratamientos</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamiento/residencial" className="hover:text-emerald-400 transition-colors">Residencial</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Cognitivo-Conductual</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                <span className="text-emerald-400 font-medium">Estándar de oro</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Terapia{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                Cognitivo-Conductual
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              El tratamiento con mayor evidencia científica para adicciones. 
              Combina lo mejor de la terapia cognitiva y conductual para 
              resultados duraderos.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Award className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">#1 en evidencia</p>
                <p className="text-slate-400 text-sm">científica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">3-6 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Brain className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">Integral</p>
                <p className="text-slate-400 text-sm">enfoque</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Shield className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">70-85%</p>
                <p className="text-slate-400 text-sm">efectividad</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?approach=cognitivo-conductual"
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Centros con TCC
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Por qué es el Estándar de Oro?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La TCC es el tratamiento más recomendado por organizaciones internacionales de salud mental.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-emerald-500/20"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                  <principle.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{principle.title}</h3>
                <p className="text-slate-400">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Fases del Tratamiento
            </h2>
          </div>

          <div className="space-y-4">
            {phases.map((phase, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white">{phase.name}</h3>
                    <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-sm rounded-full">
                      {phase.duration}
                    </span>
                  </div>
                  <p className="text-slate-400">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Techniques */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Técnicas que Aprenderás
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {techniques.map((technique, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300">{technique}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Sparkles className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra Centros con TCC
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              La mayoría de los centros de calidad utilizan TCC. Busca el que mejor se adapte a tus necesidades.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros
              </Link>
              <Link 
                to="/tratamiento/residencial"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5" />
                Otros Enfoques
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Enfoques Terapéuticos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Cognitivo", path: "/tratamiento/residencial/cognitivo", color: "text-purple-400" },
              { title: "Conductual", path: "/tratamiento/residencial/conductual", color: "text-orange-400" },
              { title: "Holístico", path: "/tratamiento/residencial/holistico", color: "text-green-400" },
              { title: "Integrativo", path: "/tratamiento/residencial/integrativo", color: "text-pink-400" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className={`font-medium ${item.color}`}>{item.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CognitivoConductualPage;
